/**
 * schemas/document.ts — ใบเสนอราคา และ ใบเสร็จรับเงิน/ใบกำกับภาษี
 *
 * จุดต่างจากระบบเดิม: client ไม่ต้องส่ง subtotal/vatAmount/totalAmount มา
 * server จะคำนวณเองจาก items ด้วย computeDocumentTotals() เพื่อกันข้อมูลเพี้ยน
 */
import { z } from 'zod';
import { QUOTATION_STATUSES, SOURCE_TYPES, TAX_INVOICE_STATUSES } from '../constants.js';
import { branchCode, dateInput, dateRangeQuery, money, optionalId, optionalText, paginationQuery, quantity, requiredText, taxId, } from './common.js';
// ==================== รายการในเอกสาร ====================
export const documentItemInputSchema = z.object({
    productName: requiredText('ชื่อรายการ', 300),
    description: optionalText(1000),
    quantity: quantity.default(1),
    unit: optionalText(50),
    unitPrice: money.default(0),
    /** ส่วนลดต่อบรรทัด เป็นจำนวนเงิน */
    discount: money.default(0),
});
/** ข้อมูลคู่ค้าที่ snapshot ไว้ในเอกสาร ไม่เปลี่ยนตาม master data ที่แก้ทีหลัง */
const customerSnapshot = {
    customerId: optionalId,
    customerName: requiredText('ชื่อลูกค้า', 200),
    customerAddress: optionalText(500),
    customerTaxId: taxId,
    customerBranch: branchCode,
    customerPhone: optionalText(100),
};
const documentBase = {
    date: dateInput,
    ...customerSnapshot,
    /** ส่วนลดท้ายบิล เป็นจำนวนเงิน */
    discountAmount: money.default(0),
    notes: optionalText(2000),
    items: z
        .array(documentItemInputSchema)
        .min(1, 'ต้องมีรายการอย่างน้อย 1 รายการ')
        .max(200, 'รายการเกินจำนวนที่ระบบรองรับ'),
};
// ==================== ใบเสนอราคา ====================
export const quotationInputSchema = z.object({
    ...documentBase,
    /** ถ้าไม่ส่งมา server จะตั้งเป็นวันที่เอกสาร + 15 วัน */
    validUntil: dateInput.nullish(),
    status: z.enum(QUOTATION_STATUSES).default('draft'),
});
export const quotationListQuery = paginationQuery.merge(dateRangeQuery).extend({
    search: optionalText(200),
    status: z.enum(QUOTATION_STATUSES).optional(),
});
// ==================== ใบเสร็จรับเงิน / ใบกำกับภาษี ====================
export const taxInvoiceInputSchema = z.object({
    ...documentBase,
    status: z.enum(TAX_INVOICE_STATUSES).default('issued'),
    /** อ้างอิงใบเสนอราคาต้นทาง (ถ้าออกต่อจากใบเสนอราคา) */
    sourceType: z.enum(SOURCE_TYPES).nullish(),
    sourceId: optionalId,
});
export const taxInvoiceListQuery = paginationQuery.merge(dateRangeQuery).extend({
    search: optionalText(200),
    status: z.enum(TAX_INVOICE_STATUSES).optional(),
});
/** สร้างใบกำกับภาษีจากใบเสนอราคาโดยตรง — server ดึงข้อมูลจากต้นทางเอง */
export const issueFromQuotationSchema = z.object({
    quotationId: z.string().trim().min(1),
    /** วันที่ของใบกำกับภาษี ถ้าไม่ส่งใช้วันนี้ */
    date: dateInput.optional(),
});
//# sourceMappingURL=document.js.map